import {
  AutoForm,
  AutoFormCheckboxGroupField,
  AutoFormInputField,
  Button,
  Checkbox
} from "@9amhealth/shared";
import { useBloc as useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import FunnelKey from "src/constants/funnelKey";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import type Path from "src/constants/path";
import { useFocusHeading, getHeadingProps } from "src/lib/useFocusHeading";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import {
  ConversionEvent,
  TrackEvent,
  TrackType
} from "src/state/Track/TrackCubit";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import UserCubit from "src/state/UserCubit/UserCubit";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import { tracker, useBloc } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import Track from "src/ui/components/Track/Track";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";
import Link from "../Link/Link";

export interface Props {
  onSuccess?: () => unknown;
  onLoadingChange?: (loading: boolean) => void;
  loginLink: Path | (() => unknown);
  title?: TranslationKey;
  description?: TranslationKey;
  buttonLabel?: TranslationKey;
  scopeKey?: FunnelKey;
  limitWidth?: boolean;
}

const PASSWORD_MIN_LENGTH = 12;

const RegisterFormSchema = z.object({
  email: z.string().email(translate("invalid_email")),
  password: z.string().refine((val) => val.length >= PASSWORD_MIN_LENGTH, {
    message: translate("password.requirements", {
      context: "error",
      min: PASSWORD_MIN_LENGTH
    })
  }),
  consentSet1: z.array(z.literal("consentSet1"), {
    message: translate("registerConsent.set1", { context: "error" })
  }),
  consentSet2: z.array(z.literal("consentSet2"), {
    message: translate("registerConsent.set2", { context: "error" })
  })
});

const FormLayout = styled.div`
  display: grid;
  gap: 1rem;
  text-align: left;
  width: min(100%, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
  margin: 0 auto;

  .data-field-error {
    padding-bottom: 0;
  }
`;

const ButtonPosition = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
`;

const RegisterForm: FC<Props> = ({
  onSuccess,
  onLoadingChange,
  loginLink,
  ...props
}) => {
  const authStage = useUserAuthStage();
  const [user] = useBloc(UserCubit, { subscribe: false });
  const [, { register }] = useBlocNext(AuthenticationBloc);
  const [, { loadSoleraUserInfo }] = useBloc(UserCubit);

  const [prefillEmail, setPrefillEmail] = React.useState(user.userData?.email);
  const [isPrefilledEmailDisabled, setIsPrefilledEmailDisabled] =
    React.useState(Boolean(user.userData?.email));

  const { headingRef } = useFocusHeading();

  React.useEffect(() => {
    const loadSoleraUserInformation = async () => {
      if (props.scopeKey === FunnelKey.solera && !prefillEmail) {
        const result = await loadSoleraUserInfo();
        setPrefillEmail(result?.email);
        setIsPrefilledEmailDisabled(false);
      }
    };
    void loadSoleraUserInformation();
  }, [props.scopeKey]);

  const handleSubmit = (values: {
    email: string;
    password: string;
    consentSet1: "consentSet1"[];
    consentSet2: "consentSet2"[];
  }) => {
    onLoadingChange?.(true);
    register(
      {
        email: values.email,
        password: values.password
      },
      props.scopeKey
    )
      .then(() => {
        onSuccess?.();
        tracker.trackConversion({
          event: ConversionEvent.FunnelRegisterCompleted
        });
      })
      .catch((error: unknown) => {
        onLoadingChange?.(false);
        reportErrorSentry(error);
      });
  };

  const { title, description } = props;

  const loginLinkProps = {
    to: typeof loginLink === "function" ? "#" : loginLink,
    onClick: typeof loginLink === "function" ? loginLink : undefined
  };

  const trackData = props.scopeKey
    ? {
        "Funnel Key": props.scopeKey
      }
    : {};

  return (
    <div>
      {authStage !== "fullAuth" && (
        <Track
          event={TrackEvent.registration}
          type={TrackType.start}
          data={trackData}
          backend={true}
        />
      )}

      <nine-heading
        style={{
          "--section-max-width": `${APP_CONTENT_WIDTH_WITHOUT_PADDING}px`
        }}
      >
        <h1 className="as-h4-large" ref={headingRef} {...getHeadingProps()}>
          {title ? (
            <Translate msg={title} />
          ) : (
            <Translate msg="create_account_title" />
          )}
        </h1>
        {description && (
          <>
            <nine-spacer s="md"></nine-spacer>
            <p className="m0 color-c-80">
              <Translate msg={description} />
            </p>
          </>
        )}
      </nine-heading>

      <nine-spacer s="xl"></nine-spacer>
      <AutoForm
        schema={RegisterFormSchema}
        onSubmit={(values) => handleSubmit(values)}
        initialValue={{ email: prefillEmail }}
        key={prefillEmail}
      >
        <FormLayout>
          <AutoFormInputField
            name="email"
            // type="email"
            isDisabled={isPrefilledEmailDisabled}
            label={translate("emailAddress")}
          />
          <AutoFormInputField
            name="password"
            label={translate("password")}
            isPasswordToggleEnabled
            type="password"
            description={
              <>
                &bull;{" "}
                <Translate
                  msg="password.requirements"
                  variables={{ min: PASSWORD_MIN_LENGTH }}
                />
              </>
            }
          />
          <div className="space"></div>

          <AutoFormCheckboxGroupField name="consentSet1">
            <Checkbox value={"consentSet1"}>
              <Translate msg="registerConsent.set1" />
            </Checkbox>
          </AutoFormCheckboxGroupField>
          <AutoFormCheckboxGroupField name="consentSet2">
            <Checkbox value={"consentSet2"}>
              <Translate msg="registerConsent.set2" />
            </Checkbox>
          </AutoFormCheckboxGroupField>
          <ButtonPosition>
            <Button type="submit">
              <Translate msg="confirm" />
            </Button>
          </ButtonPosition>
          <nine-content>
            <div className="center">
              <nine-spacer s="sm"></nine-spacer>
              <p className="m0 color-c-80">
                <Translate msg="login.alternative.question" />
              </p>
              <nine-spacer s="xxxs"></nine-spacer>
              <nine-spacer s="xxxs"></nine-spacer>
              <Link className="color-c-80" {...loginLinkProps}>
                <Translate msg="login.alternative.link" />
              </Link>
            </div>
          </nine-content>
        </FormLayout>
      </AutoForm>
    </div>
  );
};

export default RegisterForm;
